import { useAppSelector } from 'store/hooks';
import { PermissionTypes } from 'features/permissions/types';
import { selectLocalUser } from 'features/users/usersSlice';
import { useCallback } from 'react';
import { lookupDynamicPermission } from 'features/permissions/utils/lookupDynamicPermission';
import { refinePermissions } from 'features/permissions/utils/refinePermissions';
import {
  HasDynamicPermissionsOptions,
  HasPermissionsOptions,
} from 'features/permissions/utils/checkPermissions';
import { selectPermissionsState } from 'features/permissions/permissionsSlice';
import { useSystemSession } from 'hooks/useSystemSession';

export const usePermissions = () => {
  const permissionsState = useAppSelector(selectPermissionsState);
  const localUser = useAppSelector(selectLocalUser);
  const isSystemSession = useSystemSession();

  const hasPermissions = useCallback(
    (permissions: PermissionTypes | PermissionTypes[], options: HasPermissionsOptions = {}) =>
      refinePermissions(permissions, {
        permissionsState,
        localUser,
        isSystemSession,
        ...options,
      }),
    [localUser, permissionsState, isSystemSession]
  );

  const hasDynamicPermissions = useCallback(
    (
      permissions: PermissionTypes | PermissionTypes[],
      options: HasDynamicPermissionsOptions = {}
    ) => {
      const { targetUser, mode = 'any' } = options;
      const dynamicPermissions = targetUser
        ? targetUser.dynamicPermissions
        : localUser?.dynamicPermissions;

      return lookupDynamicPermission({
        permission: permissions,
        dynamicPermissions,
        mode,
      });
    },
    [localUser]
  );

  return {
    hasPermissions,
    hasDynamicPermissions,
  };
};
